/**
 * FichePhotos — galerie de 2 photos maximum pour une fiche matériel/salle ou
 * véhicule (item mairie 3.10).
 *
 * Décisions actées (22/07) :
 *   · 2 photos MAX, libres (une vue de face, une de côté, un avant/après…).
 *   · ULTRA-COMPRESSÉES avant upload (1280 px / q 0.7 ≈ 150-250 Ko au lieu de
 *     4 Mo) via compressPhotoFiche — la compression se fait dans le navigateur,
 *     le serveur ne recompresse pas.
 *   · INTERNES : ce bloc n'apparaît que côté mairie, jamais dans le catalogue
 *     vu par l'administré qui réserve.
 *
 * Le composant est agnostique de l'entité : on lui passe les 4 appels API
 * (list/add/remove/signedUrl), ce qui évite de dupliquer la galerie entre le
 * catalogue matériel et le parc véhicules.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiException } from '@/lib/api';
import { compressPhotoFiche } from '@/lib/image-compress';
import { Button, Card } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { Camera, Trash2, ImageOff, Loader2 } from 'lucide-react';
import type { FichePhoto } from '@roronoa-collectivites/shared-types';

export const FICHE_PHOTOS_MAX = 2;

export interface FichePhotosApi {
  list: (ficheId: string) => Promise<{ items: FichePhoto[] }>;
  add: (ficheId: string, file: File) => Promise<FichePhoto>;
  remove: (ficheId: string, photoId: string) => Promise<void>;
  signedUrl: (ficheId: string, photoId: string) => Promise<{ url: string }>;
}

export function FichePhotos({
  ficheId,
  api,
  editable = true,
  titre = 'Photos',
}: {
  ficheId: string;
  api: FichePhotosApi;
  /** false = lecture seule (pas d'ajout ni de suppression). */
  editable?: boolean;
  titre?: string;
}) {
  const { confirm, dialogElement } = useConfirmDialog();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [photos, setPhotos] = useState<FichePhoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.list(ficheId);
      setPhotos(res.items);
      // Résolution des URLs signées en parallèle (échec unitaire toléré).
      const entries = await Promise.all(
        res.items.map(async (p) => {
          try {
            const { url } = await api.signedUrl(ficheId, p.id);
            return [p.id, url] as const;
          } catch { return [p.id, ''] as const; }
        })
      );
      setUrls(Object.fromEntries(entries));
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement des photos impossible');
    } finally {
      setLoading(false);
    }
  }, [api, ficheId]);

  useEffect(() => { void charger(); }, [charger]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    e.target.value = ''; // permet de re-choisir le même fichier
    if (!raw) return;
    if (photos.length >= FICHE_PHOTOS_MAX) {
      setError(`Maximum ${FICHE_PHOTOS_MAX} photos. Supprimez-en une d'abord.`);
      return;
    }
    setBusy(true); setError(null);
    try {
      const file = await compressPhotoFiche(raw);
      await api.add(ficheId, file);
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Envoi de la photo impossible');
    } finally {
      setBusy(false);
    }
  }

  async function supprimer(p: FichePhoto) {
    const ok = await confirm({
      title: 'Supprimer cette photo ?',
      description: 'Elle sera retirée de la fiche.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.remove(ficheId, p.id);
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Suppression impossible');
    } finally {
      setBusy(false);
    }
  }

  const plein = photos.length >= FICHE_PHOTOS_MAX;

  return (
    <Card variant="glass" padded>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">{titre}</h3>
          <p className="text-xs text-ink-subtle">
            {FICHE_PHOTOS_MAX} maximum · usage interne (non visible par les administrés)
          </p>
        </div>
        {editable && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy || plein}
            onClick={() => inputRef.current?.click()}
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <Camera className="h-4 w-4" aria-hidden="true" />}
            Ajouter
          </Button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        capture="environment"
        className="hidden"
        onChange={(e) => void onPick(e)}
      />

      {error && <p className="mb-2 text-sm text-danger">{error}</p>}

      {loading ? (
        <p className="py-4 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <ImageOff className="h-7 w-7 text-ink-dim" aria-hidden="true" />
          <p className="text-sm text-ink-muted">Aucune photo.</p>
          {editable && (
            <p className="text-xs text-ink-dim">
              Ajoutez-en jusqu'à {FICHE_PHOTOS_MAX} (ex. une vue d'ensemble et un détail).
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <figure key={p.id} className="group relative overflow-hidden rounded-r-md border border-border bg-surface/50">
              {urls[p.id] ? (
                <img
                  src={urls[p.id]}
                  alt="Photo de la fiche"
                  loading="lazy"
                  className="aspect-video w-full object-cover"
                />
              ) : (
                <div className="flex aspect-video w-full items-center justify-center">
                  <ImageOff className="h-6 w-6 text-ink-dim" aria-hidden="true" />
                </div>
              )}
              {editable && (
                <button
                  type="button"
                  onClick={() => void supprimer(p)}
                  disabled={busy}
                  aria-label="Supprimer cette photo"
                  className="absolute right-2 top-2 rounded-r-sm bg-surface/90 p-1.5 text-ink-muted transition-colors hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </figure>
          ))}
        </div>
      )}
      {dialogElement}
    </Card>
  );
}
